/**
 * The SHELL's maximum width — the outer bound on `layouts/Default`'s `<main>` and
 * on anything `position: fixed` that has to stay attached to it.
 *
 * ⚠️ This is the SHELL's cap, not a page's content cap. That is `CONTENT_MAX_WIDTH`
 * below — the same NUMBER as of 2026-08-24, but a different thing. Reach for
 * `CONTENT_MAX_WIDTH` when you are sizing CONTENT; reach for this one only when you
 * mean the shell itself: `layouts/Default`'s row cap, or a `position: fixed` bar
 * that has to stay attached to it (`BottomNav`).
 *
 * NOT a phone width, and the first attempt at this got it wrong in a way worth
 * recording. `apps/app` is the consumer certificate app — reached by tapping an
 * NFC tag with a phone, every Figma frame drawn at 375, bottom drawers and a
 * thumb-reachable bottom nav — so the obvious reading of "centre column" was
 * "clamp it to a phone and centre that on desktop". Measured at 1100px, the
 * certificates list already renders a THREE-COLUMN grid: the app has a genuine
 * desktop layout and always did. A 480px clamp did not centre that layout, it
 * squeezed it, and the cards visibly overlapped.
 *
 * So the clamp is a CEILING on a layout that already responds, not a cage that
 * forces one. 1280 keeps the desktop grid intact at every width it was designed
 * for and only starts binding on wide monitors, where the alternative is a list
 * that runs edge-to-edge across 2560px. It is inert on a phone.
 *
 * Anything `position: fixed` must clamp to this too, or it spans the viewport
 * while the content it belongs to sits centred. See `BottomNav`.
 */
export const APP_MAX_WIDTH = 1280

/**
 * Width of the desktop left nav rail (`components/UI/SideNav`), in px.
 *
 * ## 240 -> 280 (2026-08-31)
 *
 * User directive: the rail wants to be wider. **280 was chosen after seeing 256 live.**
 *
 * This shipped at 256 first, matching `apps/platform`'s `SIDEBAR_WIDTH`, and the
 * argument for that is preserved below because it is still the best CONSISTENCY case.
 * It lost on two counts once rendered:
 *
 *   1. **+16px is 6.7% — below the threshold of noticing.** The directive was that the
 *      rail reads as narrow. A change nobody can see does not answer that, and the
 *      request would simply come back.
 *   2. **The consistency argument is weaker than it looks.** `apps/platform` is a
 *      desktop admin console where the rail is load-bearing; `apps/app` is phone-first
 *      (§37) and this rail is a SECONDARY layout. They are not the same furniture doing
 *      the same job, so they are not obliged to agree — and, as the note below already
 *      concedes, platform's 256 is itself undocumented. Matching an unjustified number
 *      is not a justification.
 *
 * Verified at 280 before committing: the content grid still holds three columns at 1280
 * and the search field / first card right edges stay equal. The content column pays
 * 40px rather than 16.
 *
 * The 256 reasoning is kept verbatim below — it is the argument to re-read if anyone
 * later proposes converging the two apps' rails deliberately, rather than by default.
 *
 * 256 is `apps/platform`'s `SIDEBAR_WIDTH` (`config/layout.ts`), i.e. the width the
 * OTHER app's nav already ships at. Matching it beats inventing a value: the two rails
 * are the same furniture in the same product, and a reader moving between the consumer
 * app and the brand console meets one sidebar width instead of two near-misses.
 *
 * ⚠️ Be honest about the strength of that: platform's 256 is settled by USE, not by
 * argument — it is a bare undocumented constant over there, with no note saying why.
 * So this is a consistency argument, not a derived one. What makes it still the best
 * available is that the alternative is a number with NO argument at all, and that the
 * rail's own content cannot settle it either way (see the label-column arithmetic
 * below: there was already ~64px of slack at 240, so nothing here is driven by need).
 * 256 is also Tailwind's `w-64`, which is the same "least surprising value" reasoning
 * the 200 -> 240 step used.
 *
 * It is a MODEST step — +16px, ~6.7%, against the previous step's +40px/20%. That is
 * deliberate (it is the cheapest change that lands on a value with a reason), but it
 * does mean the widening is subtle. If a more pronounced rail is wanted, this is a
 * one-line change and the cost scales linearly with it — 280 would cost 40px of
 * content at 1280 instead of 16.
 *
 * Cost at the SHIPPED 280 (this paragraph is live, not history — the rest of the 256
 * block above it is the preserved argument): at a 1280 viewport the content column goes
 * 1040 -> 1000, the label column 136 -> 176, and the "content stops shrinking" threshold
 * (`APP_MAX_WIDTH + SIDE_NAV_WIDTH`) moves 1520 -> 1560.
 *
 * At 176px against a 71.97px longest label there is now ~104px of slack, so the
 * content-driven argument for widening is entirely spent. Any FURTHER change here is
 * proportion alone, and should say so rather than reaching for the label column again.
 *
 * ## 200 -> 240 (2026-08-23), and why 240 specifically — the previous step, kept
 *
 * User directive: *"make nav wider on desktop - too narrow."*
 *
 * 240 is the conventional sidebar width — it is MUI's own documented `drawerWidth`
 * default and Tailwind's `w-60` — so it is the value a reader is least surprised by.
 * The rail's own content does NOT independently prove 200 was too narrow, and the
 * honest version of that argument is worth writing down rather than the flattering
 * one. The shell pads 24px a side and `RailRow` pads 12px more, with a 20px glyph
 * and a 12px gap ahead of the label, so the LABEL COLUMN is
 * `SIDE_NAV_WIDTH - 48 - 24 - 32` — **96px at 200**, and "Certificates", the longest
 * of the three destinations, MEASURES 71.97px at the rail's 14px. That is ~24px of
 * slack, which is about four characters: tight enough that a future destination
 * ("Notifications", ~78px) would nearly fill it and the row's existing
 * `textOverflow: 'ellipsis'` would start earning its keep, but NOT the "one label
 * away from clipping" that an earlier draft of this note claimed. 240 took the
 * label column to 136px, and 256 takes it to 152. Treat the headroom as the supporting
 * argument and the convention plus the user's read of the proportion as the primary
 * one — at 152px against a 71.97px longest label there is now ~80px of slack, so the
 * content argument is firmly spent and any FURTHER widening is proportion alone.
 *
 * ## The cost, and why it does not break the search-field / card-edge alignment
 *
 * The alignment `HeaderBar` maintains — its search field's right edge landing exactly
 * on the first grid card's right edge — is preserved for ANY value of this constant,
 * by algebra rather than by luck. Both sides derive from the same quantity:
 *
 *   content column  C  = min(100vw - SIDE_NAV_WIDTH, APP_MAX_WIDTH)
 *   grid inner width   = C - 2*(PAGE_GUTTER - CONTENT_INNER_PAD) - 2*CONTENT_INNER_PAD
 *                      = C - 48
 *   card width         = (C - 48 - 2*CONTENT_INNER_PAD) / 3   = (C - 80) / 3
 *   field cap          = (C - 48 - 32) / 3                    = (C - 80) / 3
 *
 * — identical expressions, and both boxes start at the same left edge. So the two
 * move together, for any rail width AND any gutter. The coupling that the comment at
 * `PAGE_GUTTER` warns about is between the GUTTER and the literals `48px`/`32px` in
 * `HeaderBar`, which a rail change does not touch but a GUTTER change does.
 *
 * ⚠️ The `- 96` / `- 128` this block used to carry were the INDEX gutter's, from when
 * the list took a 48px gutter and detail pages took 24. That distinction is gone
 * (see `CONTENT_MAX_WIDTH`) and every page is on `PAGE_GUTTER` now, so the constants
 * halved. MEASURED after that change: at a 1280 viewport the field and the first card
 * are 320px each, both running 264 -> 584; at 2105 both are 400px, both running
 * 556.5 -> 956.5.
 *
 * Above a `APP_MAX_WIDTH + SIDE_NAV_WIDTH` viewport no WIDTH changes: the row cap
 * grows with the rail (1480 at a 200 rail -> 1520 at 240 -> 1536 at 256), C is pinned
 * at `APP_MAX_WIDTH` either way, and the extra width comes out of the page's own
 * margin. Every column keeps its size — but it does SHIFT, by half the delta, because
 * the row is centred. So "free above the cap" means free in width and half-the-delta
 * in position (+20px for the 40px step, +8px for the 16px one); the width is only ever
 * paid between `md` and the cap. (The worked example that used to sit here measured
 * the certificate page at 1600 as 1072 wide at both rail widths. The WIDTH figure is
 * stale — that page is on the full `CONTENT_MAX_WIDTH` now, so it is 1232 there —
 * but the shift-not-shrink point it made is unchanged.)
 *
 * The other measured costs at 1280, for the record: the content column moves
 * 224 -> 1256 to 264 -> 1256 (right edge unchanged, left edge in by 40), and the
 * certificate's 5fr/7fr split goes media 413 -> 396.66, record 579 -> 555.34.
 *
 * ⚠️ THIS COSTS CONTENT WIDTH, and the cost is not hypothetical — read this before
 * changing it.
 *
 * The rail is in FLOW, to the left of the content column, so below a viewport of
 * `APP_MAX_WIDTH + SIDE_NAV_WIDTH` the content column is `100vw - SIDE_NAV_WIDTH`
 * rather than `100vw`. At or above that width the rail sits in the page's own
 * margin and the content column is a full `APP_MAX_WIDTH`, unchanged.
 *
 * There is no arrangement that avoids this. At a 1280 viewport, 1280px of content
 * plus a rail does not fit; the only alternatives are overlapping the content or
 * not having a rail. What the `APP_MAX_WIDTH + SIDE_NAV_WIDTH` cap buys is that the
 * cost is paid only where it is unavoidable.
 *
 * **The cost is paid on EVERY signed-in page, uniformly** — that is the point, not a
 * regression. The rail used to hide on pushed screens, which bought a certificate
 * detail page 200px of extra width and, in exchange, misaligned it from the list it
 * was reached from by 144px and left desktop users with no persistent navigation at
 * all. Uniform is what makes the columns line up by construction. Measured cost on
 * the page that pays the most, the certificate's two-column split at a 1280
 * viewport: media 430 → 413 → 396.66 → 390, record 602 → 579 → 555.34 → 546, across
 * no rail → a 200 rail → a 240 rail → today's 256 (a `5fr / 7fr` grid, so both narrow
 * proportionally and neither reflows). The 256 pair was MEASURED 2026-08-31 on a
 * certificate at a 1280 viewport, not extrapolated, and it reconciles: 390 + 546 +
 * 48 (gutters) + 40 (grid gap) = the 1024 content column exactly, and 390/546 is 5/7.
 * See `showsDesktopRail` in `components/UI/primaryNav`.
 *
 * ## The dependency this creates
 *
 * `HeaderBar`'s search field derives its width from the CARD width, and expresses
 * that as `calc((min(100vw, APP_MAX_WIDTH) - 48px - 32px) / 3)` — i.e. it uses the
 * VIEWPORT as a proxy for the content column. That proxy was exact before the rail
 * existed and is exact again above the cap, but between `md` and the cap the
 * content column is `100vw - SIDE_NAV_WIDTH` and the proxy overshoots, so the
 * search field renders wider than one card.
 *
 * The fix — APPLIED, and it is the form in `HeaderBar` today:
 *
 *   md: `calc((min(100vw - ${SIDE_NAV_WIDTH}px, ${APP_MAX_WIDTH}px) - 48px - 32px) / 3)`
 *
 * The `48px` is `PAGE_GUTTER` doubled and the `32px` is `CONTENT_INNER_PAD` doubled —
 * both deliberately left as literals there, and both documented at those constants.
 * (They were `96px`/`32px` while the list carried its own wider gutter; see
 * `CONTENT_MAX_WIDTH` for why that gutter is gone.)
 *
 * `HeaderBar`'s own comment states the contract this is honouring: "This and the
 * grid are two expressions of ONE layout, and nothing links them but this comment.
 * If CertificatesGrid's breakpoints, spacing, or the page gutter change, this must
 * change with them."
 */
export const SIDE_NAV_WIDTH = 280

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * THE CONTENT WIDTH — ONE NUMBER, EVERY PAGE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Every page's content column is 1280 with a `PAGE_GUTTER` on each side. There is
 * no second width and no per-page choice; the whole point of writing that down is
 * that the next page must not invent one.
 *
 * ## This replaced a TWO-width rule on 2026-08-24, and why
 *
 * Until then a page picked between `INDEX_MAX_WIDTH` (1280, "a grid of cards") and
 * `READING_MAX_WIDTH` (1120, "a record you read"), with the certificates list on
 * the first and everything else on the second. The rule was coherent on paper and
 * did not survive measurement.
 *
 * **1. It inverted itself at every viewport people actually use.** The INDEX also
 * carried a WIDER gutter (48 vs 24), so below a 1520px viewport — where neither cap
 * binds and both columns are `100vw - SIDE_NAV_WIDTH` — the "wide" index rendered
 * NARROWER than the "narrow" reading column. MEASURED at 1280: index content 944,
 * reading content 992. The distinction was not merely inert there, it ran backwards.
 *
 * **2. The reading cap was not buying a reading measure.** The argument for 1120 is
 * line length in prose. The certificate page's one prose element is the product
 * Description (`ProductTabContent`), which fills the record column: at the 1120 cap
 * that column is 602px, and at `font.body` (16px Roboto, MEASURED at 7.03px/char)
 * that is **86 characters per line** — already well past the 45–75 the argument
 * appeals to. So the cap paid 112px of photograph to land a measure that was too
 * wide anyway. A prose measure has to be applied to the PROSE (as `PROSE_MAX_WIDTH`
 * does), not to the page container; the container was the wrong instrument.
 *
 * **3. What the certificate page's width actually feeds.** Its columns are
 * `minmax(0, 5fr) minmax(0, 7fr)` — a product PHOTOGRAPH and a column of
 * label/value rows plus chevron rows. Both gain from width the same way a card grid
 * does. On a 2105px monitor the 1120 cap left ~372px of empty margin per side while
 * the photograph sat at 430px, which is what prompted this change.
 *
 * THE COST THIS INCURRED, now paid: the Description ran **99 characters per line**
 * at the 1280 cap, up from 86. Both were past the 45–75 band, so the old cap had
 * already been failing at the reading-measure job it was justified by — widening
 * it made a pre-existing miss visible rather than creating one. It is fixed where
 * it belongs, on the PROSE element rather than on any container: see
 * `READING_MEASURE`, which derives 512 from the narrowest face a tenant can pick
 * rather than inventing a per-surface number.
 *
 * ## Where the number binds
 *
 * The cap rarely binds at laptop widths, which is worth knowing before measuring at
 * 1280 and concluding the container is broken. `available` is
 * `100vw - SIDE_NAV_WIDTH` on every signed-in page (`showsDesktopRail` in
 * `components/UI/primaryNav` keeps the rail up everywhere), so at a 1280 viewport
 * every column is `1280 - SIDE_NAV_WIDTH` and the cap does nothing. A true 1280 needs a
 * viewport above **`APP_MAX_WIDTH + SIDE_NAV_WIDTH`** — 1536 today. That threshold moves
 * with the rail every time it changes (1480 at a 200 rail, 1520 at 240, 1536 at 256), so
 * it is written symbolically here rather than as a number that has already been wrong
 * twice.
 *
 * A corollary of collapsing to one width AND one gutter: every page now shares the
 * same left and right content edges at every viewport. That was unreachable before —
 * below 1520 the gutters differed by 24px a side, and above 1520 the caps differed
 * by 160 — and it is the reason the gutter was unified in the same edit rather than
 * left at 48 for the list.
 *
 * ## Why this lives here rather than in the page that invented one
 *
 * 1120 was declared as `DESKTOP_MAX_WIDTH` inside `pages/CertificatePage/index.tsx`
 * — one file, private to that page. That is the drift mechanism, not a detail:
 * with the number owned by a page, every OTHER page had to either import from a
 * page (which nobody does) or invent its own. They invented their own, and by
 * 2026-08 a 1280-viewport walk through the app crossed FOUR content widths:
 *
 *   certificates list   944  (a 48px gutter, from a 32px page pad + a 16px inner)
 *   a certificate       992  (1040 less its 24px gutters)
 *   its sub-views      1232  (no cap at all — `p: 2` and the whole column)
 *   Profile / Activity  640  (a prose measure — see PROSE_MAX_WIDTH)
 *
 * — the certificate and its own Provenance / Scans / Activity screens, one tap
 * apart, 240px apart in width. `components/UI/PageContainer` reads these constants
 * and every page goes through it.
 *
 * ⚠️ Changing `PAGE_GUTTER` is NOT a one-line edit. It also moves `DESKTOP_TOP` in
 * `pages/CertificatePage` (which is `NAV_HEADER_HEIGHT + DESKTOP_GUTTER`, so a
 * vertical change too) and it must be mirrored into `HeaderBar`'s search-field
 * `calc` in the same commit — see `PAGE_GUTTER`.
 */
export const CONTENT_MAX_WIDTH = APP_MAX_WIDTH

/**
 * The content column's side gutter at `md` and up — every page, since the INDEX/
 * READING split was collapsed (see `CONTENT_MAX_WIDTH`).
 *
 * ⚠️ COUPLED to `HeaderBar`'s search-field cap, which spells this out as the `48px`
 * (this doubled) in
 * `calc((min(100vw - ${SIDE_NAV_WIDTH}px, ${APP_MAX_WIDTH}px) - 48px - 32px) / 3)`.
 * Written with the constant names rather than their current values on purpose: an
 * earlier draft of this line quoted `100vw - 240px`, and the moment the rail moved to
 * 256 that comment described a calc the code no longer produced. `HeaderBar`
 * interpolates the constants, so only the prose could ever drift here. Nothing
 * but that comment and this one links them, and when they disagree the search field
 * stops landing on the first card's right edge — it was 66.67px out on 2026-08-23
 * for exactly this reason. Change one, change the other, in the same edit, and
 * re-measure both edges.
 *
 * ON THE CERTIFICATES LIST IT IS DELIVERED IN THREE PARTS, which is why that page
 * passes `PAGE_GUTTER - CONTENT_INNER_PAD` (8) to `PageContainer` rather than this:
 * its `HeaderBar` pads itself 16px and the grid wrapper is `p-4`, so the container
 * only owes the remaining 8. Every other page passes nothing and gets the full 24
 * from the container.
 *
 * It was 24 for detail pages and 48 for the list. The list's 48 existed because "a
 * 1280 column with a 24px gutter reads as edge-to-edge" — but the list only ever
 * REACHED 1280 above a 1520px viewport, and below that the wider gutter made the
 * card grid the narrowest column in the app (MEASURED at 1280: 944 against every
 * other page's 992). One gutter is what makes every page share its edges.
 */
export const PAGE_GUTTER = 24

/**
 * The content column's side gutter below `md`.
 *
 * Held separate so the desktop gutter can move without touching a phone — the
 * surface this app is actually used on (working-conventions §37). Every page that
 * adopted `PageContainer` was on 16px at `xs` already, so the unification is a
 * desktop-only change by construction.
 */
export const PAGE_GUTTER_MOBILE = 16

/**
 * A PROSE measure, narrower than either content width, for the four pages that are
 * a single stack of form fields and preference rows: Profile, Notifications, the
 * Activity feed, My code.
 *
 * ## This is not a second content width
 *
 * It is a constraint applied INSIDE the content column, not an alternative to it.
 * Those four pages take `CONTENT_MAX_WIDTH` like every other page — their gutter and
 * their outer cap come from `PageContainer` — and then hold their rows to 640 within
 * it. The one-width rule above is unaffected, and widening the container above did
 * not move them: both boxes centre on the same axis, so the 640 column is where it
 * was, at every viewport. (VERIFIED after the 1120 -> 1280 collapse.)
 *
 * ## Why it was kept rather than unified onto the full column
 *
 * A preference row at 1232px puts its label and its switch most of a screen apart.
 * 640 was the one width in the drift list above that was CHOSEN rather than
 * inherited, and widening it is a change nobody asked for.
 *
 * ⚠️ IT IS NOT A READING MEASURE, and this comment used to claim otherwise — the
 * second half of the argument above read "these pages carry real paragraphs
 * (Notifications' 'security and sign-in emails are always sent' caveat) that would
 * run past every line-length guideline there is", implying 640 was where those
 * paragraphs stop doing that. MEASURED: at 640, `font.body` Roboto runs **90
 * characters per line** — further past the 45–75 band than the 86 the certificate
 * page's old 1120 cap produced, which is the number that got that cap deleted. So
 * 640 fails at line length by the same test, only less visibly.
 *
 * What 640 actually buys is ROW geometry: a label and its switch close enough
 * together to read as one control. That is a real and sufficient reason to keep
 * it. It is just not the reason that was written down. For running prose, use
 * `READING_MEASURE`.
 *
 * The nesting is a visual NO-OP: both boxes centre on the same axis, so a 640
 * column lands in exactly the same pixels it did under those pages' old 32px
 * gutters, at every viewport. What it buys is that the gutter and the outer cap
 * stop being four pages' private business.
 *
 * ⚠️ ONE KNOWN INCONSISTENCY, deliberate and unresolved: the Activity FEED holds
 * 640 while the certificate's Activity SUB-VIEW — the same event rows — sits on the
 * full column (1280). Whichever way that resolves, both should move together. Flipping
 * a page to the full column is deleting one `maxWidth` at its call site.
 */
export const PROSE_MAX_WIDTH = 640

/**
 * A READING MEASURE — the cap for a run of actual PROSE, applied to the prose
 * element itself rather than to any container.
 *
 * This is the number `CONTENT_MAX_WIDTH` left open. Collapsing the INDEX/READING
 * split widened the certificate's record column, which took the product
 * Description from 86 characters per line to **99** — worse, and flagged as a
 * decision rather than absorbed, because picking a width inside a page is exactly
 * the drift this file exists to stop. So it is picked HERE, once, and derived
 * rather than taste.
 *
 * ## Why 512, and why it is a MEASUREMENT and not a preference
 *
 * The target is the 45–75 characters-per-line band. The complication is that
 * `apps/app` is multi-tenant: a brand picks its own face in Studio
 * (`fontOptions` — Roboto, Lato, Noto Sans, Inter, Agbalumo, Montserrat), and the
 * same pixel width yields a different character count in each. Measured at 16px
 * via canvas `measureText` over a sample of representative English prose (NOT
 * lorem, whose letter frequencies are not English's), average advance per
 * character:
 *
 *   Lato 6.83 · Roboto 7.10 · Agbalumo 7.15 · Noto Sans 7.51 · Inter 7.60 · Montserrat 7.99
 *
 * The band's ceiling is therefore set by the NARROWEST face, since it fits the
 * most characters into a given width: `75 x 6.83 = 512`. At 512 every one of the
 * six lands inside the band — Lato 75, Roboto 72, Agbalumo 72, Noto Sans 68,
 * Inter 67, Montserrat 64 — and 520 already pushes Lato to 76. So 512 is not a
 * round number someone liked; it is the largest cap under which no tenant can
 * fall out of the band.
 *
 * Deliberately at the ceiling rather than at Bringhurst's 66: the Description is
 * one field among label/value rows and chevron rows that run the full record
 * column, so a measure much narrower than its neighbours reads as an indented
 * block rather than as the page's prose. The ceiling is the minimum intervention
 * that satisfies the constraint; anything narrower is taste with nothing behind
 * it.
 *
 * ## Why px and NOT `ch`, which is the answer that sounds right
 *
 * `ch` is font-relative, so the obvious argument is that it self-corrects across
 * tenant fonts where a px value cannot. MEASURED, it does not: `ch` is the
 * advance of the `0` glyph, prose scales with the lowercase average, and the two
 * do not track — the ratio runs 1.22 (Noto Sans) to 1.36 (Lato) across the same
 * six faces. A `59ch` cap, chosen to give Roboto the same 75, puts THREE of the
 * six over the ceiling (Lato 80, Inter 78, Montserrat 78), where 512px puts none.
 * The intuitive unit is the worse one here, and only measuring says so.
 *
 * ## Where it applies
 *
 * Running prose — a paragraph someone reads left to right. Today: the product
 * Description (`ProductTabContent`). NOT lists, rows, labels, or a page title.
 *
 * Known candidates not yet moved, because each is a visual change on a surface
 * that would want reviewing on its own: the page descriptions rendered by
 * `components/UI/PageHeader` (§40), which sit inside `PROSE_MAX_WIDTH`'s 640 and
 * so run to ~90 characters by the arithmetic above.
 */
export const READING_MEASURE = 512

/**
 * The 16px that the certificates list's own children already contribute to the
 * gutter, and that its card grid uses as its inter-card gap.
 *
 * ONE number with three expressions, which is why it is here rather than repeated:
 * `HeaderBar` pads `24px 16px 0`, the grid wrapper is Tailwind `p-4`, and
 * `CertificatesGrid` is `<Grid container spacing={2}>` — `apps/app` is on MUI's
 * DEFAULT 8px base, not `@repo/ui`'s 4px (working-conventions §29), so `spacing={2}`
 * is 16px here.
 *
 * The list's `PageContainer` subtracts it — `gutter = PAGE_GUTTER - CONTENT_INNER_PAD`
 * — so the two halves add up to `PAGE_GUTTER`. `HeaderBar`'s field cap spells the
 * same numbers out as literals (`48px` = 2 * PAGE_GUTTER, `32px` = 2 * this); see
 * `PAGE_GUTTER` for why that expression is deliberately left alone.
 */
export const CONTENT_INNER_PAD = 16
